import { createAsyncThunk } from '@reduxjs/toolkit'
import { isCommonResponseSuccessful, isCommonResponseSuccessfulV2 } from 'src/mentor/helpers/utilityFunctions'
import { getMPDataWithParams } from 'src/mentor/services'
import { getSPDataWithParams } from 'src/supportPortal/services/services'
import { getAllRolesInBoolean } from 'src/mentor/layout/utils'
import { normalizePrescriptionViewSettings } from '../utils/prescriptionViewSettingsDefaults'
import {
  selectPrescriptionViewSettingsData,
  setPrescriptionViewSettingsData,
  setPrescriptionViewSettingsError,
  setPrescriptionViewSettingsState,
} from '../prescriptionReducer'

const inFlightPrescriptionViewSettingsRequests = new Map()
const prescriptionViewSettingsCache = new Map()
const PRESCRIPTION_VIEW_SETTINGS_CACHE_TTL_MS = 5 * 60 * 1000

function getPrescriptionViewSettingsRequestKey({
  currentPortal,
  emrReportType,
  practitionerId,
  practitionerSlug,
}) {
  return `${currentPortal || 'mentor'}|${emrReportType || 'prescription'}|${practitionerId || ''}|${practitionerSlug || ''}`
}

function withPrescriptionViewSettingsMeta(settings, responseData = {}) {
  const normalized = normalizePrescriptionViewSettings(settings)
  return {
    ...normalized,
    _prescriptionViewSettingsExists:
      responseData?.prescriptionViewSettingsExists ?? !responseData?.prescriptionViewSettingsCreatedWithDefaults,
    _prescriptionViewSettingsCreatedWithDefaults:
      responseData?.prescriptionViewSettingsCreatedWithDefaults ?? false,
  }
}

/**
 * Detects the current portal based on the window pathname
 * @returns {string|null} Portal type or null if cannot be determined
 */
function detectCurrentPortal() {
  if (typeof window === 'undefined') return null
  const pathname = window.location.pathname
  if (pathname.startsWith('/support-home') || pathname.startsWith('/support/')) {
    return 'support'
  }
  if (pathname.startsWith('/mentor-home') || pathname.startsWith('/mentor/')) {
    return 'mentor'
  }
  return null
}

// Thunk
export const fetchPrescriptionViewSettings = createAsyncThunk(
  'prescriptionStore/fetchPrescriptionViewSettings',
  async ({ emrReportType = 'prescription', practitionerId, practitionerSlug } = {}, { getState, dispatch }) => {
    const state = getState()
    const existingState = selectPrescriptionViewSettingsData(state)?.state
    const currentPortal = detectCurrentPortal()
    const requestKey = getPrescriptionViewSettingsRequestKey({
      currentPortal,
      emrReportType,
      practitionerId,
      practitionerSlug,
    })
    // Sticky-failure guard: once a fetch has failed in this session, don't
    // re-attempt on every render. Effect-based callers (e.g.
    // ViewOnlyPrescriptionV2) re-evaluate on each state change and would
    // otherwise loop on 403/500 forever. Caller can force a retry by
    // dispatching setPrescriptionViewSettingsState('idle') first.
    if (existingState === 'error' || existingState === 'failed') {
      return
    }

    // Mentor-portal endpoint requires ROLE_TEACHER/ROLE_MENTOR (enforced by
    // PractitionerBaseController). Short-circuit for non-practitioners so
    // they don't generate a 403 storm — see web.stdout.log around 06:03-06:15
    // where one client produced 2,547 403s on this endpoint.
    if (currentPortal === 'mentor') {
      const roles = getAllRolesInBoolean() || {}
      const isPractitioner = !!(roles.isTeacher || roles.isDoctor || roles.isTherapist || roles.isScreener)
      if (!isPractitioner) {
        dispatch(setPrescriptionViewSettingsError('not-a-practitioner'))
        dispatch(setPrescriptionViewSettingsState('error'))
        return
      }
    }

    dispatch(setPrescriptionViewSettingsState('loading'))

    const cached = prescriptionViewSettingsCache.get(requestKey)
    if (cached && Date.now() - cached.timestamp < PRESCRIPTION_VIEW_SETTINGS_CACHE_TTL_MS) {
      dispatch(setPrescriptionViewSettingsData(cached.data))
      dispatch(setPrescriptionViewSettingsState('success'))
      return cached.data
    }

    try {
      let requestPromise = inFlightPrescriptionViewSettingsRequests.get(requestKey)
      if (!requestPromise) {
        if (currentPortal === 'support') {
          // Support portal API - requires practitioner-id or practitioner-slug
          if (!practitionerId && !practitionerSlug) {
            // In support portal, if no practitionerId or practitionerSlug provided, don't fetch (will use prop settings)
            dispatch(setPrescriptionViewSettingsState('error'))
            return
          }
          const params = {
            'emr-based-report-type': emrReportType,
          }
          if (practitionerSlug) {
            params['practitioner-slug'] = practitionerSlug
          } else if (practitionerId) {
            params['practitioner-id'] = practitionerId
          }
          requestPromise = getSPDataWithParams(
            '/api/secure/cc/support/practitioner/settings/final-report-view',
            params,
            undefined,
            undefined,
            true,
          )
          inFlightPrescriptionViewSettingsRequests.set(requestKey, requestPromise)
        } else {
          // Mentor portal API (default)
          requestPromise = getMPDataWithParams(
            '/api/secure/practitioner/emr-settings/settings/final-report-view',
            {
              'emr-based-report-type': emrReportType,
            },
          )
          inFlightPrescriptionViewSettingsRequests.set(requestKey, requestPromise)
        }
      }

      let resp
      try {
        resp = await requestPromise
      } finally {
        inFlightPrescriptionViewSettingsRequests.delete(requestKey)
      }

      if (currentPortal === 'support') {
        if (resp?.status === 200 && isCommonResponseSuccessfulV2(resp)) {
          // Support portal response format
          const data = resp?.data?.data || resp?.data
          const prescriptionViewSettings = data?.prescriptionViewSettings || data
          if (prescriptionViewSettings) {
            const normalizedSettings = withPrescriptionViewSettingsMeta(prescriptionViewSettings, data)
            dispatch(setPrescriptionViewSettingsData(normalizedSettings))
            dispatch(setPrescriptionViewSettingsState('success'))
            prescriptionViewSettingsCache.set(requestKey, {
              data: normalizedSettings,
              timestamp: Date.now(),
            })
          } else {
            throw new Error('Invalid response from API')
          }
        } else {
          throw new Error('Invalid response from API')
        }
      } else {
        if (resp?.status === 200 && isCommonResponseSuccessful(resp?.data?.code)) {
          // Update store silently with new data
          const responseData = resp?.data?.data || {}
          const resolvedSettings = responseData?.prescriptionViewSettings
          const normalizedSettings = withPrescriptionViewSettingsMeta(resolvedSettings, responseData)
          dispatch(setPrescriptionViewSettingsData(normalizedSettings))
          dispatch(setPrescriptionViewSettingsState('success'))
          prescriptionViewSettingsCache.set(requestKey, {
            data: normalizedSettings,
            timestamp: Date.now(),
          })
        } else {
          throw new Error('Invalid response from API')
        }
      }
    } catch (error) {
      dispatch(setPrescriptionViewSettingsError(error.message || 'Failed to fetch prescription view settings'))
      dispatch(setPrescriptionViewSettingsState('error'))
    }
  },
)
